import axios from 'axios'

/**
 * Forma típica del cuerpo de error que devuelve el backend NestJS.
 * Ej: { message: 'No autorizado', error: 'Unauthorized', statusCode: 401 }
 * `message` puede ser un string o un array de strings (errores de class-validator).
 */
interface ApiErrorBody {
    message?: string | string[]
    error?: string
    statusCode?: number
}

const isApiErrorBody = (value: unknown): value is ApiErrorBody => {
    return typeof value === 'object' && value !== null
}

const extractMessage = (body: ApiErrorBody): string | undefined => {
    const { message } = body

    if (Array.isArray(message)) {
        const joined = message.filter((m) => typeof m === 'string').join('. ')
        return joined.length > 0 ? joined : undefined
    }

    if (typeof message === 'string' && message.length > 0) {
        return message
    }

    return undefined
}

/**
 * Obtiene un mensaje de error legible a partir de un error desconocido.
 *
 * Cubre los casos frecuentes en la app:
 * - AxiosError: se lee `error.response.data.message`.
 * - Objeto plano lanzado como `error.response.data` (patrón de src/services).
 * - Instancia de Error nativa.
 * - String.
 *
 * @param error Error capturado (unknown).
 * @param fallback Mensaje por defecto si no se puede extraer uno específico.
 */
export const getApiErrorMessage = (
    error: unknown,
    fallback = 'Ocurrió un error inesperado'
): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data
        if (isApiErrorBody(data)) {
            const message = extractMessage(data)
            if (message !== undefined) {
                return message
            }
        }
        if (typeof error.message === 'string' && error.message.length > 0) {
            return error.message
        }
        return fallback
    }

    if (isApiErrorBody(error)) {
        const message = extractMessage(error)
        if (message !== undefined) {
            return message
        }
    }

    if (error instanceof Error && error.message.length > 0) {
        return error.message
    }

    if (typeof error === 'string' && error.length > 0) {
        return error
    }

    return fallback
}
